import { menu, Menu, MenuHandler, MenuItem, MenuList } from '@material-tailwind/react'
import { EditorContent, useEditor, Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import useMobile from 'hooks/useMobile'
import debounce from 'lodash.debounce'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useMediaQuery } from 'react-responsive'
import SimpleBar from 'simplebar-react'
import { ReactSetterType } from 'utils/types'

const MenuBar = ({ editor, setMenubarHeight }:{editor : Editor | null, setMenubarHeight : ReactSetterType<number> }) => {

    const handleRect = useCallback((node : HTMLDivElement | undefined) => {
      setMenubarHeight(node?.clientHeight || 0);
    }, []);

    if (!editor) {
      return null
    }

  return (
    <div ref={handleRect as any} className='tiptap-menubar flex gap-x-2 p-4 border-b border-slate-400'>
      <button
        onClick={() => editor.chain().focus().toggleBold().run()}
        disabled={!editor.can().chain().focus().toggleBold().run()}
        className={editor.isActive('bold') ? 'is-active' : ''}
      >
        <i className='fas fa-bold'></i>
      </button>
      <button
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={!editor.can().chain().focus().toggleItalic().run()}
        className={editor.isActive('italic') ? 'is-active' : ''}
      >
        <i className='fas fa-italic'></i>
      </button>
      <button
        onClick={() => editor.chain().focus().toggleStrike().run()}
        disabled={!editor.can().chain().focus().toggleStrike().run()}
        className={editor.isActive('strike') ? 'is-active' : ''}
      >
        <i className='fas fa-strikethrough'></i>
      </button>
      <button onClick={() => editor.chain().focus().unsetAllMarks().run()}>
        <i className='fas fa-eraser'></i>
      </button>
      <button
        onClick={() => editor.chain().focus().setParagraph().run()}
        className={editor.isActive('paragraph') ? 'is-active' : ''}
      >
        <i className='fas fa-align-left'></i>
      </button>
      <Menu>
        <MenuHandler>
            <button className={(editor.isActive('heading') ? 'is-active' : '') + ' tiptap-btn'}>
                <i className='fas fa-heading'></i>
            </button>
        </MenuHandler>
        <MenuList>
            <MenuItem
              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
              className={editor.isActive('heading', { level: 1 }) ? 'tiptap-btn-is-active' : ''}
            >
              Header 1
            </MenuItem>
            <MenuItem
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              className={editor.isActive('heading', { level: 2 }) ? 'tiptap-btn-is-active' : ''}
            >
              Header 2
            </MenuItem>
            <MenuItem onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={editor.isActive('heading', { level: 3 }) ? 'tiptap-btn-is-active' : ''}>
              Header 3
            </MenuItem>
            <MenuItem
              onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
              className={editor.isActive('heading', { level: 4 }) ? 'tiptap-btn-is-active' : ''}
            >
              Header 4
            </MenuItem>
            <MenuItem
              onClick={() => editor.chain().focus().toggleHeading({ level: 5 }).run()}
              className={editor.isActive('heading', { level: 5 }) ? 'tiptap-btn-is-active' : ''}
            >
              Header 5
            </MenuItem>
            <MenuItem
              onClick={() => editor.chain().focus().toggleHeading({ level: 6 }).run()}
              className={editor.isActive('heading', { level: 6 }) ? 'tiptap-btn-is-active' : ''}
            >
              Header 6
            </MenuItem>
        </MenuList>
      </Menu>
      <button
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={editor.isActive('bulletList') ? 'is-active' : ''}
      >
        <i className='fas fa-list-ul'></i>
      </button>
      <button
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={editor.isActive('orderedList') ? 'is-active' : ''}
      >
        <i className='fas fa-list-ol'></i>
      </button>
      <button className='text-sm' onClick={() => editor.chain().focus().setHorizontalRule().run()}>
        line
      </button>
    </div>
  )
}

type Prop = {
    tabIndex ?:number,
    text : string,
    setText : ReactSetterType<string>,
    rawText : string,
    setRawText : ReactSetterType<string>,
    className? : string,
    placeholder? : string,
}

const Tiptap = ({
    tabIndex,
    rawText,
    setRawText,
    setText,
    text,
    className = "",
    placeholder = "<h2>Hi there,</h2><p>Write Your Text Here</p>"
}:Prop) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
    ],
    onUpdate: ({editor})=>{
        const text = editor.getText();
        const html = editor.getHTML();
        setRawText(text);
        setText(html);
    },
    editorProps: {
      attributes: {
        class: 'px-3 prose prose-sm sm:prose md:prose-base xl:prose-lg m-auto focus:outline-none min-h-full bg-white prose-p:mb-0 prose-p:mt-0',
      },
    },
    content: text || placeholder
  })
  
  const isMobile = useMediaQuery({ query: '(max-width: 960px)' });
  const [menubarHeight,setMenubarHeight] = useState(0);
  const [simplebarHeight, setSimplebarHeight] = useState<number | undefined>(0);

  const simplebar = useRef<SimpleBar>(null)
  const rootDiv = useRef<HTMLDivElement>(null)

  useEffect(()=>{
    if(menubarHeight === 0)
        return;
    if(isMobile)
        return setSimplebarHeight(undefined)
    setSimplebarHeight((rootDiv.current?.clientHeight || 0) - menubarHeight - 4)// 4 is a hacky fix (bad) for absolute position so the editor wouldnt overflow
  }, [menubarHeight, isMobile])

  const handleResize = ()=>{
    const recalculateSimplebar = debounce(()=>{
        if(isMobile)
            return setSimplebarHeight(undefined)
        console.log(menubarHeight, rootDiv.current?.clientHeight)
        setSimplebarHeight((rootDiv.current?.clientHeight || 0) - menubarHeight - 4)
    },20)
    window.addEventListener('resize', recalculateSimplebar)
    return ()=>{
      window.removeEventListener('resize', recalculateSimplebar)
    }
  } 

  

  useEffect(handleResize, [])
  useEffect(handleResize, [isMobile])

  return (
    <div ref={rootDiv} className={className + " relative lg:flex flex-col"}>
        <MenuBar editor={editor} setMenubarHeight={setMenubarHeight}/>
        <div className='hidden lg:block grow' onClick={()=>{editor?.chain().focus().run()}}></div>
        <div className="rounded-b-md lg:rounded-none lg:absolute lg:top-[calc(4rem+3px)] cursor-text w-full" onClick={()=>{editor?.chain().focus().run()}}>    
            <SimpleBar ref={simplebar} style={{height : simplebarHeight}}>
                <EditorContent tabIndex={tabIndex} editor={editor} onFocus={()=>{editor?.commands.setContent(text, false);  editor?.commands.focus('end')}}/>
            </SimpleBar>
        </div>
    </div>
  )
}

export default Tiptap